/**
 * Hook — shared market WebSocket.
 *
 * One socket per browser tab, shared by every component that calls
 * `useMarketWebSocket()` (the header index strip, Market Watch, Portfolio,
 * Option Chain). Subscriptions are reference-counted per consumer, so a page
 * that unmounts drops only the tokens nothing else still wants. The socket
 * opens with the first consumer, survives navigation while the header strip is
 * mounted, and closes when the last consumer unmounts or the user logs out.
 *
 * Protocol: `/ws/market-feed?token=<jwt>`, JSON `{action: subscribe|unsubscribe|ping, tokens}`;
 * ticks arrive as `{type: 'tick', data}` with Kotak's short keys and are pushed
 * into the Zustand market store.
 */

import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react';
import { api } from '@/config/api';
import { useAuthStore } from '@/stores/auth-store';
import { useMarketStore } from '@/stores/market-store';

/** Server closes with this code when the JWT is missing or expired. */
const WS_CLOSE_UNAUTHORIZED = 4401;
const MAX_RETRIES = 10;
const BASE_DELAY_MS = 1000;
const PING_INTERVAL_MS = 30_000;

interface FeedState {
  isConnected: boolean;
  /** Whether the backend attached the broker (Kotak) feed on the last subscribe; null until known. */
  feedAttached: boolean | null;
  /** Server-side explanation when the broker feed is not attached. */
  feedMessage: string;
}

interface UseWebSocketReturn extends FeedState {
  subscribe: (tokens: string[]) => void;
  unsubscribe: (tokens: string[]) => void;
  reconnect: () => void;
}

// ── Shared connection state (module singleton) ───────────────────────────────

let state: FeedState = { isConnected: false, feedAttached: null, feedMessage: '' };
const listeners = new Set<() => void>();

function setState(patch: Partial<FeedState>) {
  state = { ...state, ...patch };
  listeners.forEach((listener) => listener());
}

function subscribeListener(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot() {
  return state;
}

const consumers = new Map<number, Set<string>>();
let nextConsumerId = 1;

let ws: WebSocket | null = null;
let wantConnection = false;
let retryCount = 0;
let authRetries = 0;
let retryTimer: ReturnType<typeof setTimeout> | null = null;
let pingTimer: ReturnType<typeof setInterval> | null = null;

function allTokens(): string[] {
  const union = new Set<string>();
  for (const tokens of consumers.values()) for (const token of tokens) union.add(token);
  return [...union];
}

function holders(token: string): number {
  let count = 0;
  for (const tokens of consumers.values()) if (tokens.has(token)) count += 1;
  return count;
}

function send(data: Record<string, unknown>) {
  if (ws?.readyState === WebSocket.OPEN) ws.send(JSON.stringify(data));
}

function wsUrl() {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const token = localStorage.getItem('access_token') || '';
  return `${protocol}//${window.location.host}/ws/market-feed?token=${token}`;
}

function stopPing() {
  if (pingTimer) {
    clearInterval(pingTimer);
    pingTimer = null;
  }
}

function startPing() {
  stopPing();
  pingTimer = setInterval(() => send({ action: 'ping' }), PING_INTERVAL_MS);
}

function clearRetry() {
  if (retryTimer) {
    clearTimeout(retryTimer);
    retryTimer = null;
  }
}

interface FeedMessage {
  type?: string;
  data?: Record<string, string | number | undefined>;
  feed_attached?: boolean;
  message?: string;
}

function handleMessage(event: MessageEvent) {
  let message: FeedMessage;
  try {
    message = JSON.parse(event.data) as FeedMessage;
  } catch {
    return; // ignore non-JSON frames
  }

  if (message.type === 'tick' && message.data) {
    const d = message.data;
    const str = (...keys: string[]): string | undefined => {
      for (const key of keys) {
        const value = d[key];
        if (value !== undefined && value !== null && value !== '') return String(value);
      }
      return undefined;
    };
    const token = str('tk', 'instrument_token') || '';
    if (!token) return;
    useMarketStore.getState().updateTick(token, {
      instrument_token: token,
      trading_symbol: str('ts', 'trading_symbol') || '',
      exchange_segment: str('es', 'exchange_segment') || '',
      ltp: str('ltp') || '0',
      open: str('o', 'open') || '0',
      high: str('h', 'high') || '0',
      low: str('l', 'low') || '0',
      close: str('c', 'close') || '0',
      volume: str('v', 'volume') || '0',
      change: str('chg', 'change') || '0',
      change_percent: str('chgp', 'change_percent') || '0',
      total_buy_qty: str('tbq', 'total_buy_qty'),
      total_sell_qty: str('tsq', 'total_sell_qty'),
      best_bid_price: str('bp', 'best_bid_price'),
      best_bid_qty: str('bq', 'best_bid_qty'),
      best_ask_price: str('ap', 'best_ask_price'),
      best_ask_qty: str('aq', 'best_ask_qty'),
      open_interest: str('oi', 'open_interest'),
      last_traded_qty: str('ltq', 'last_traded_qty'),
      last_traded_time: str('ltt', 'last_traded_time'),
      updated_at: str('t', 'updated_at'),
    });
    return;
  }

  if (message.type === 'subscribed' && typeof message.feed_attached === 'boolean') {
    setState({ feedAttached: message.feed_attached, feedMessage: message.message || '' });
  }
  // pong and other messages are handled silently
}

function connect() {
  if (!wantConnection) return;
  if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) return;
  clearRetry();

  const socket = new WebSocket(wsUrl());
  ws = socket;

  socket.onopen = () => {
    if (ws !== socket) return;
    retryCount = 0;
    authRetries = 0;
    setState({ isConnected: true, feedMessage: '' });
    startPing();
    const tokens = allTokens();
    if (tokens.length > 0) send({ action: 'subscribe', tokens });
  };

  socket.onmessage = handleMessage;

  socket.onclose = (event) => {
    if (ws === socket) ws = null;
    stopPing();
    setState({ isConnected: false });
    if (!wantConnection) return;

    // The server rejected our access token (expired): let the axios
    // interceptor refresh it, then reconnect with the new one.
    if (event.code === WS_CLOSE_UNAUTHORIZED && authRetries < 3) {
      authRetries += 1;
      setState({ feedMessage: 'Session token expired — refreshing…' });
      api
        .get('/auth/me')
        .catch(() => undefined)
        .finally(() => {
          retryTimer = setTimeout(connect, 300);
        });
      return;
    }

    if (retryCount < MAX_RETRIES) {
      const delay = Math.min(BASE_DELAY_MS * 2 ** retryCount, 30_000);
      retryTimer = setTimeout(() => {
        retryCount += 1;
        connect();
      }, delay);
    }
  };

  socket.onerror = () => {
    // onclose fires after onerror
  };
}

function closeSocket() {
  stopPing();
  clearRetry();
  const socket = ws;
  ws = null;
  if (socket) socket.close();
  setState({ isConnected: false });
}

function disconnect() {
  wantConnection = false;
  retryCount = 0;
  closeSocket();
  setState({ feedAttached: null });
}

function reconnectShared() {
  closeSocket();
  retryCount = 0;
  authRetries = 0;
  wantConnection = true;
  // Small delay so the old socket fully closes.
  retryTimer = setTimeout(connect, 200);
}

function addConsumer(): number {
  const id = nextConsumerId++;
  consumers.set(id, new Set());
  wantConnection = true;
  connect();
  return id;
}

function removeConsumer(id: number) {
  const tokens = consumers.get(id);
  consumers.delete(id);
  if (tokens && tokens.size > 0) {
    const orphaned = [...tokens].filter((token) => holders(token) === 0);
    if (orphaned.length > 0) send({ action: 'unsubscribe', tokens: orphaned });
  }
  if (consumers.size === 0) disconnect();
}

function subscribeTokens(id: number, tokens: string[]) {
  const mine = consumers.get(id);
  if (!mine || tokens.length === 0) return;
  for (const token of tokens) mine.add(token);
  send({ action: 'subscribe', tokens });
}

function unsubscribeTokens(id: number, tokens: string[]) {
  const mine = consumers.get(id);
  if (!mine || tokens.length === 0) return;
  for (const token of tokens) mine.delete(token);
  const orphaned = tokens.filter((token) => holders(token) === 0);
  if (orphaned.length > 0) send({ action: 'unsubscribe', tokens: orphaned });
}

// ── Hook ─────────────────────────────────────────────────────────────────────

export function useMarketWebSocket(): UseWebSocketReturn {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const snapshot = useSyncExternalStore(subscribeListener, getSnapshot, getSnapshot);
  const consumerRef = useRef<number | null>(null);

  useEffect(() => {
    if (!isAuthenticated) return;
    const id = addConsumer();
    consumerRef.current = id;
    return () => {
      consumerRef.current = null;
      removeConsumer(id);
    };
  }, [isAuthenticated]);

  const subscribe = useCallback((tokens: string[]) => {
    if (consumerRef.current !== null) subscribeTokens(consumerRef.current, tokens);
  }, []);

  const unsubscribe = useCallback((tokens: string[]) => {
    if (consumerRef.current !== null) unsubscribeTokens(consumerRef.current, tokens);
  }, []);

  const reconnect = useCallback(() => {
    reconnectShared();
  }, []);

  return {
    isConnected: snapshot.isConnected,
    feedAttached: snapshot.feedAttached,
    feedMessage: snapshot.feedMessage,
    subscribe,
    unsubscribe,
    reconnect,
  };
}
